import React, {useEffect, useMemo, useRef, useState} from "react";
import {getDomain} from "tldts";
import {LoadingImage} from "../App";
import "./Foxy.css";

const FUNCTION_URL = "/.netlify/functions/foxy";
const STORAGE_KEY = "foxy-active-sites";

const SOURCES = [
    {id: "main", label: "Основные"},
    {id: "other", label: "Другое"},
    {id: "russia", label: "Россия"},
];

type SourceSite = {
    name: string;
    group: string;
    domains: string[];
};

type SourceData = Record<string, SourceSite>;

type SiteInfo = {
    id: string; // unique across all sources: `${source}::${key}`
    key: string; // original site name from opencck, e.g. "chatgpt.com"
    source: string;
    group: string;
    domains: string[]; // unique registrable domains for this site
};

type FoxyEntry = {
    include: "include";
    type: "wildcard";
    title: string;
    pattern: string;
    active: boolean;
};

function displayName(key: string): string {
    return key.replace(/^www\./, "").replace(/\.com$/, "");
}

function uniqueRegistrableDomains(hostnames: string[]): string[] {
    const set = new Set<string>();
    for (const hostname of hostnames) {
        const domain = getDomain(hostname);
        if (domain) set.add(domain);
    }
    return Array.from(set).sort();
}

function loadActiveSites(): Record<string, boolean> | null {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

function groupBy(sites: SiteInfo[]): [string, SiteInfo[]][] {
    const map = new Map<string, SiteInfo[]>();
    sites.forEach((site) => {
        const list = map.get(site.group) || [];
        list.push(site);
        map.set(site.group, list);
    });
    return Array.from(map.entries());
}

function Foxy() {
    const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
    const [error, setError] = useState<string>("");
    const [sites, setSites] = useState<SiteInfo[]>([]);
    const [activeSites, setActiveSites] = useState<Record<string, boolean>>({});

    useEffect(() => {
        document.title = "Foxy — списки для прокси";

        const link = document.querySelector("link[rel~='icon']");
        if (link) link.setAttribute("href", "./favicon.ico");

        Promise.all(
            SOURCES.map((src) =>
                fetch(`${FUNCTION_URL}?source=${src.id}`)
                    .then(async (response) => {
                        if (!response.ok) throw new Error(`${src.label}: HTTP ${response.status}`);
                        const data = (await response.json()) as SourceData;
                        return Object.entries(data).map(([key, site]): SiteInfo => ({
                            id: `${src.id}::${key}`,
                            key,
                            source: src.id,
                            group: site.group,
                            domains: uniqueRegistrableDomains(site.domains || []),
                        }));
                    })
            )
        )
            .then((perSource) => {
                const parsedSites = perSource.flat();
                parsedSites.sort((a, b) => a.group.localeCompare(b.group) || a.key.localeCompare(b.key));

                const saved = loadActiveSites();
                const initialActive: Record<string, boolean> = {};
                parsedSites.forEach((site) => {
                    initialActive[site.id] = saved ? saved[site.id] !== false : true;
                });

                setSites(parsedSites);
                setActiveSites(initialActive);
                setStatus("success");
            })
            .catch((err) => {
                console.error(err);
                setError(String(err?.message || err));
                setStatus("error");
            });
    }, []);

    useEffect(() => {
        if (status === "success") {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(activeSites));
        }
    }, [activeSites, status]);

    const sections = useMemo(
        () =>
            SOURCES.map((src) => ({
                ...src,
                groups: groupBy(sites.filter((site) => site.source === src.id)),
            })).filter((section) => section.groups.length > 0),
        [sites]
    );

    const output = useMemo<FoxyEntry[]>(() => {
        const domainSet = new Set<string>();
        sites.forEach((site) => {
            if (!activeSites[site.id]) return;
            site.domains.forEach((domain) => domainSet.add(domain));
        });
        return Array.from(domainSet)
            .sort()
            .map((domain) => ({
                include: "include",
                type: "wildcard",
                title: domain,
                pattern: `*${domain}/*`,
                active: true,
            }));
    }, [sites, activeSites]);

    const outputJson = useMemo(() => JSON.stringify(output, null, 2), [output]);

    const toggleSite = (id: string) => {
        setActiveSites((prev) => ({...prev, [id]: !prev[id]}));
    };

    const setAll = (value: boolean) => {
        const next: Record<string, boolean> = {};
        sites.forEach((site) => (next[site.id] = value));
        setActiveSites(next);
    };

    const setGroup = (groupSites: SiteInfo[], value: boolean) => {
        setActiveSites((prev) => {
            const next = {...prev};
            groupSites.forEach((site) => (next[site.id] = value));
            return next;
        });
    };

    const copyJson = async () => {
        try {
            await navigator.clipboard.writeText(outputJson);
        } catch (err) {
            console.error(err);
        }
    };

    const downloadJson = () => {
        const blob = new Blob([outputJson], {type: "application/json"});
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "foxy-list.json";
        a.click();
        URL.revokeObjectURL(url);
    };

    if (status === "loading") {
        return (
            <div style={{padding: 20}}>
                <p className="Foxy-intro">Генерация конфига для импорта в FoxyProxy</p>
                {LoadingImage()}
            </div>
        );
    }

    if (status === "error") {
        return (
            <div style={{padding: 20}}>
                <p className="Foxy-intro">Генерация конфига для импорта в FoxyProxy</p>
                <p style={{color: "salmon"}}>Не удалось загрузить список: {error}</p>
                <p>Если это локальная разработка через `npm start` — функции Netlify не проксируются, нужно запускать через `netlify dev`.</p>
            </div>
        );
    }

    return (
        <div className="Foxy">
            <p className="Foxy-intro">Генерация конфига для импорта в FoxyProxy</p>

            <div className="Foxy-toolbar">
                <button onClick={() => setAll(true)}>Включить всё</button>
                <button onClick={() => setAll(false)}>Выключить всё</button>
                <span className="Foxy-count">Доменов в выдаче: {output.length}</span>
            </div>

            {sections.map((section) => (
                <div className="Foxy-section" key={section.id}>
                    <h2 className="Foxy-section-title">{section.label}</h2>
                    <div className="Foxy-groups">
                        {section.groups.map(([group, groupSites]) => (
                            <FoxyGroup
                                key={`${section.id}-${group}`}
                                group={group}
                                sites={groupSites}
                                activeSites={activeSites}
                                onToggleSite={toggleSite}
                                onSetGroup={(value: boolean) => setGroup(groupSites, value)}
                            />
                        ))}
                    </div>
                </div>
            ))}

            <div className="Foxy-output">
                <div className="Foxy-toolbar">
                    <button onClick={copyJson}>Скопировать JSON</button>
                    <button onClick={downloadJson}>Скачать JSON</button>
                </div>
                <textarea readOnly value={outputJson}/>
            </div>
        </div>
    );
}

function FoxyGroup({
    group,
    sites,
    activeSites,
    onToggleSite,
    onSetGroup,
}: {
    group: string;
    sites: SiteInfo[];
    activeSites: Record<string, boolean>;
    onToggleSite: (id: string) => void;
    onSetGroup: (value: boolean) => void;
}) {
    const activeCount = sites.filter((site) => activeSites[site.id]).length;
    const allActive = activeCount === sites.length;
    const someActive = activeCount > 0 && !allActive;
    const headerCheckboxRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (headerCheckboxRef.current) {
            headerCheckboxRef.current.indeterminate = someActive;
        }
    }, [someActive]);

    return (
        <div className="Foxy-group">
            <label className="Foxy-group-header">
                <input
                    ref={headerCheckboxRef}
                    type="checkbox"
                    checked={allActive}
                    onChange={() => onSetGroup(!allActive)}
                />
                <h3>{group}</h3>
                <span className="Foxy-group-count">{activeCount}/{sites.length}</span>
            </label>
            <div className="Foxy-group-sites">
                {sites.map((site) => (
                    <label className="Foxy-site" key={site.id} title={site.key}>
                        <input
                            type="checkbox"
                            checked={!!activeSites[site.id]}
                            onChange={() => onToggleSite(site.id)}
                        />
                        <img
                            className="Foxy-favicon"
                            src={`https://www.google.com/s2/favicons?sz=32&domain=${site.key}`}
                            alt=""
                            width={16}
                            height={16}
                            loading="lazy"
                            onError={(e) => {
                                (e.currentTarget as HTMLImageElement).style.visibility = "hidden";
                            }}
                        />
                        <span className="Foxy-site-name">{displayName(site.key)}</span>
                        <span className="Foxy-domain-count">({site.domains.length})</span>
                    </label>
                ))}
            </div>
        </div>
    );
}

export default Foxy;
